import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

// Re-sync with the server every 30s; the timer ticks locally in between.
// Keeping this slow is critical for staying within Vercel's edge request limits
// (a 1s poll from an always-on OBS overlay is ~2.6M requests/month on its own).
const SYNC_INTERVAL_MS = 30000;

const TIMER_MODES = {
  COUNTDOWN: 'countdown',
  COUNTUP: 'countup',
};

function formatTime(ms) {
  const totalSeconds = Math.floor(Math.max(0, ms) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

function normalizeMode(mode) {
  return mode === TIMER_MODES.COUNTUP ? TIMER_MODES.COUNTUP : TIMER_MODES.COUNTDOWN;
}

export default function StreamTimerWidget({ compact = false, flannelStyle = false, active = true }) {
  const [searchParams] = useSearchParams();
  const urlMode = searchParams.get('mode');
  const [serverState, setServerState] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const effectiveMode = useMemo(() => {
    if (urlMode === 'countup' || urlMode === 'countdown') {
      return normalizeMode(urlMode);
    }
    return normalizeMode(serverState?.mode);
  }, [urlMode, serverState?.mode]);

  // Fetch timer state from API, then re-sync periodically while active.
  // `active` is false when the stream is offline, so an idle OBS overlay stops
  // polling entirely (it still fetches once so a preview shows current state).
  useEffect(() => {
    const fetchTimer = async () => {
      try {
        const response = await fetch('/api/admin?action=get-widget-timer-public');
        if (!response.ok) {
          throw new Error('Failed to load timer');
        }
        const data = await response.json();

        if (data.success) {
          setServerState({ ...data, fetchedAt: Date.now() });
          setError(null);
        } else {
          setError(data.error || 'Failed to load timer');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error loading timer:', err);
      } finally {
        setLoading(false);
      }
    };

    // Always fetch once so the widget shows real state, even when offline
    fetchTimer();

    if (!active) return undefined;

    const interval = setInterval(fetchTimer, SYNC_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [active]);

  // Tick locally every second while the timer is running and the stream is live
  useEffect(() => {
    if (!active || !serverState?.isRunning) return undefined;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [active, serverState]);

  // Derive the displayed state from the last server sync plus locally elapsed time
  let timerData = serverState;
  if (serverState?.isRunning) {
    const elapsed = Math.max(0, now - serverState.fetchedAt);
    const isCountUp = effectiveMode === TIMER_MODES.COUNTUP;

    if (isCountUp) {
      const elapsedTime = serverState.elapsedTime + elapsed;
      timerData = {
        ...serverState,
        mode: effectiveMode,
        elapsedTime,
        displayTime: elapsedTime,
        formattedTime: formatTime(elapsedTime),
        isExpired: false,
        isRunning: true
      };
    } else {
      const remainingTime = Math.max(0, serverState.remainingTime - elapsed);
      timerData = {
        ...serverState,
        mode: effectiveMode,
        remainingTime,
        displayTime: remainingTime,
        formattedTime: formatTime(remainingTime),
        isExpired: remainingTime <= 0,
        isRunning: remainingTime > 0
      };
    }
  } else if (serverState) {
    const displayTime = effectiveMode === TIMER_MODES.COUNTUP
      ? (serverState.elapsedTime ?? serverState.displayTime ?? 0)
      : (serverState.remainingTime ?? serverState.displayTime ?? 0);

    timerData = {
      ...serverState,
      mode: effectiveMode,
      displayTime,
      formattedTime: formatTime(displayTime),
      // Public API omits duration; trust server isExpired when the countdown has finished.
      isExpired: effectiveMode === TIMER_MODES.COUNTDOWN && (
        serverState.isExpired || (displayTime <= 0 && serverState.duration > 0)
      )
    };
  }

  const isCountUp = effectiveMode === TIMER_MODES.COUNTUP;
  const displayMs = timerData?.displayTime ?? timerData?.elapsedTime ?? timerData?.remainingTime ?? 0;
  const hasActiveTimer = isCountUp
    ? (timerData?.isRunning || displayMs > 0)
    : (timerData && (timerData.remainingTime > 0 || timerData.isRunning || timerData.isExpired));

  // Compact flannel mode
  if (compact && flannelStyle) {
    if (loading) {
      return <span className="timer-flannel-loading">--:--:--</span>;
    }

    const displayTime = !hasActiveTimer ? '00:00:00' : timerData.formattedTime;
    const isExpired = !isCountUp && timerData?.isExpired;
    const isRunning = timerData?.isRunning;

    return (
      <div className="timer-flannel">
        <span className="timer-flannel-label">TIMER</span>
        <span className={`timer-flannel-value ${isExpired ? 'expired' : ''} ${!isRunning && !isExpired ? 'paused' : ''}`}>
          {isExpired ? "TIME'S UP!" : displayTime}
        </span>
        <style>{`
          .timer-flannel {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 12px;
            font-family: 'Press Start 2P', monospace;
            width: 100%;
          }
          .timer-flannel-label {
            font-size: 0.85rem;
            color: #d4c4a8;
            text-shadow: 1px 1px 0 #0d4a54;
            letter-spacing: 0.1em;
            line-height: 1.4;
          }
          .timer-flannel-value {
            font-size: 1.13rem;
            font-weight: 700;
            color: #f5f0e6;
            font-variant-numeric: tabular-nums;
            text-shadow: 1px 1px 0 #a65241;
            letter-spacing: 0.05em;
            line-height: 1.4;
          }
          .timer-flannel-value.expired {
            color: #e8b298;
            animation: timerThrob 3s ease-in-out infinite;
          }
          .timer-flannel-value.paused {
            color: #d4c4a8;
          }
          .timer-flannel-loading {
            color: #d4c4a8;
            font-family: 'Press Start 2P', monospace;
            font-size: 0.67rem;
          }
          @keyframes timerThrob {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.4; }
          }
        `}</style>
      </div>
    );
  }

  // Regular compact mode
  if (compact) {
    if (loading) {
      return <span className="timer-compact-loading">--:--:--</span>;
    }

    if (error || !hasActiveTimer) {
      return (
        <div className="timer-compact">
          <span className="timer-compact-label">TIMER</span>
          <span className="timer-compact-value timer-compact-paused">00:00:00</span>
          <style>{`
            .timer-compact {
              display: flex;
              align-items: center;
              gap: 8px;
              font-family: 'JetBrains Mono', monospace;
            }
            .timer-compact-label {
              font-size: 0.625rem;
              color: #dc2626;
              text-transform: uppercase;
              letter-spacing: 0.05em;
              font-weight: 600;
            }
            .timer-compact-value {
              font-size: 1.125rem;
              font-weight: 700;
              color: #22d3ee;
              font-variant-numeric: tabular-nums;
            }
            .timer-compact-paused {
              color: #94a3b8;
            }
            .timer-compact-loading {
              color: #94a3b8;
              font-family: 'JetBrains Mono', monospace;
            }
          `}</style>
        </div>
      );
    }

    const { formattedTime, isRunning, isExpired } = timerData;
    const displayColor = isExpired ? '#dc2626' : (isRunning ? '#22d3ee' : '#fbbf24');
    const displayText = isExpired ? "TIME'S UP!" : formattedTime;

    return (
      <div className="timer-compact">
        <span className="timer-compact-label">TIMER</span>
        <span className="timer-compact-value" style={{ color: displayColor }}>{displayText}</span>
        <style>{`
          .timer-compact {
            display: flex;
            align-items: center;
            gap: 8px;
            font-family: 'JetBrains Mono', monospace;
          }
          .timer-compact-label {
            font-size: 0.625rem;
            color: #dc2626;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            font-weight: 600;
          }
          .timer-compact-value {
            font-size: 1.125rem;
            font-weight: 700;
            font-variant-numeric: tabular-nums;
            text-shadow: 0 0 8px currentColor;
          }
        `}</style>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-transparent p-4">
        <div className="text-white text-sm font-mono">Loading timer...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-transparent p-4">
        <div className="text-red-400 text-sm font-mono">Error: {error}</div>
      </div>
    );
  }

  if (!hasActiveTimer) {
    return (
      <div className="bg-transparent p-4 font-mono">
        <div className="timer-container">
          <div className="timer-label">{isCountUp ? 'STREAM ELAPSED' : 'STREAM TIMER'}</div>
          <div className="timer-display timer-paused">
            00:00:00
          </div>
        </div>
        <style>{`
          .timer-container {
            text-align: center;
          }
          
          .timer-label {
            font-size: 0.75rem;
            color: #94a3b8;
            text-transform: uppercase;
            letter-spacing: 0.1em;
            margin-bottom: 0.5rem;
          }
          
          .timer-display {
            font-size: 2.5rem;
            font-weight: 700;
            color: #94a3b8;
            letter-spacing: 0.1em;
            font-variant-numeric: tabular-nums;
          }
        `}</style>
      </div>
    );
  }

  const { formattedTime, isRunning, isExpired } = timerData;

  if (!isCountUp && isExpired) {
    return (
      <div className="bg-transparent p-4 font-mono">
        <div className="celebration-container">
          <div className="celebration-text celebration-glow">
            TIME'S UP!
          </div>
          <div className="celebration-subtitle">
            STREAM TIMER COMPLETE
          </div>
        </div>
        <style>{`
          .celebration-container {
            text-align: center;
            animation: celebrationPulse 1s ease-in-out infinite;
          }
          
          .celebration-text {
            font-size: 3rem;
            font-weight: 900;
            color: #22d3ee;
            text-transform: uppercase;
            letter-spacing: 0.2em;
            margin-bottom: 0.5rem;
            animation: celebrationGlow 2s ease-in-out infinite alternate;
          }
          
          .celebration-subtitle {
            font-size: 1rem;
            color: #94a3b8;
            text-transform: uppercase;
            letter-spacing: 0.1em;
          }
          
          @keyframes celebrationPulse {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.05);
            }
          }
          
          @keyframes celebrationGlow {
            0% {
              text-shadow: 0 0 10px #22d3ee, 0 0 20px #22d3ee, 0 0 30px #22d3ee;
            }
            100% {
              text-shadow: 0 0 20px #22d3ee, 0 0 30px #22d3ee, 0 0 40px #22d3ee, 0 0 50px #22d3ee;
            }
          }
        `}</style>
      </div>
    );
  }

  const labelText = isCountUp
    ? (isRunning ? 'STREAM ELAPSED' : 'STREAM ELAPSED (PAUSED)')
    : (isRunning ? 'STREAM TIMER' : 'STREAM TIMER (PAUSED)');
  const displayColor = isRunning ? '#22d3ee' : '#fbbf24';

  return (
    <div className="bg-transparent p-4 font-mono">
      <div className="timer-container">
        <div className="timer-label">{labelText}</div>
        <div className="timer-display" style={{ color: displayColor }}>
          {formattedTime}
        </div>
      </div>
      <style>{`
        .timer-container {
          text-align: center;
        }
        
        .timer-label {
          font-size: 0.75rem;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 0.5rem;
        }
        
        .timer-display {
          font-size: 2.5rem;
          font-weight: 700;
          text-shadow: 0 0 10px rgba(34, 211, 238, 0.5);
          letter-spacing: 0.1em;
          font-variant-numeric: tabular-nums;
        }
      `}</style>
    </div>
  );
}
